export type PendingEditableCellCommit = {
  fieldName: string
  rowIndex: number
  newValue: unknown
}

export function readListboxEditorPendingValue(rawValue: string): unknown {
  if (rawValue === 'true') return true
  if (rawValue === 'false') return false
  if (rawValue !== '' && /^-?\d+$/.test(rawValue)) return Number(rawValue)
  return rawValue
}

export function resolvePendingEditableCellCommit(
  tableRoot: HTMLElement | null,
  options: {
    cellEditable: boolean
    itemsLength: number
    resolveColumnName: (target: HTMLElement | null | undefined) => string
  },
): PendingEditableCellCommit | null {
  if (!options.cellEditable || !tableRoot) return null

  const active = document.activeElement
  if (!(active instanceof HTMLElement) || !tableRoot.contains(active)) return null

  const listboxWrapper = active.closest('.table-cell-editor-listbox')
  if (listboxWrapper instanceof HTMLElement) {
    const row = listboxWrapper.closest('tr')
    const tbody = row?.closest('tbody')
    if (!row || !tbody) return null

    const rowIndex = Array.from(tbody.querySelectorAll('tr')).indexOf(row)
    if (rowIndex < 0 || rowIndex >= options.itemsLength) return null

    const fieldName = options.resolveColumnName(listboxWrapper)
    if (!fieldName) return null

    const hidden = listboxWrapper.querySelector(
      '.table-cell-editor-listbox-value',
    ) as HTMLInputElement | null
    const rawValue = hidden?.value ?? listboxWrapper.dataset.selectedValue ?? ''
    const newValue = readListboxEditorPendingValue(rawValue)

    return { fieldName, rowIndex, newValue }
  }

  const isTextEditor = active.classList.contains('table-cell-editor')
  const isCheckboxEditor = active.classList.contains('table-cell-editor-checkbox')
  if (!isTextEditor && !isCheckboxEditor) return null

  const row = active.closest('tr')
  const tbody = row?.closest('tbody')
  if (!row || !tbody) return null

  const rowIndex = Array.from(tbody.querySelectorAll('tr')).indexOf(row)
  if (rowIndex < 0 || rowIndex >= options.itemsLength) return null

  const fieldName = options.resolveColumnName(active)
  if (!fieldName) return null

  const newValue = isCheckboxEditor
    ? (active as HTMLInputElement).checked
    : (active as HTMLInputElement).value

  return { fieldName, rowIndex, newValue }
}

export type EditableTableCommitHook = () => boolean | Promise<boolean>

export async function flushPendingEditableTableEditsFromRegistry(
  elements: Record<string, { formId?: string | number; commitPendingCellEdit?: EditableTableCommitHook }>,
  formId?: string | number,
  tick: () => Promise<void> = async () => {},
): Promise<boolean> {
  const wanted =
    formId != null && formId !== '' && formId !== -1 ? String(formId) : null
  let committed = false

  for (const element of Object.values(elements)) {
    if (element == null) continue
    if (wanted && String(element.formId ?? '') !== wanted) continue
    const hook = element.commitPendingCellEdit
    if (typeof hook !== 'function') continue
    const result = hook()
    const didCommit = result instanceof Promise ? await result : result
    if (didCommit) committed = true
  }

  if (committed) {
    await tick()
    await tick()
  }

  return committed
}
